/**
 * Endpoints du domaine notification (notification-service) — source canonique.
 * Chemins copiés TELS QUELS de la collection Postman / du guide §6.7
 * (préfixe gateway `/gw/notification`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 */
export const notificationEndpoints = {
  templates: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée ou remplace un modèle. */
    add: "/gw/notification/api/v1/NotificationTemplates/add",
    /**
     * GET — liste directe (pas de `.../all` ni de pagination serveur, guide §6.7).
     * Retourne l'ensemble des modèles toutes langues/canaux confondus.
     * Rôles : flashpay-superadmin, flashpay-admin.
     */
    list: "/gw/notification/api/v1/NotificationTemplates",
    /** GET — modèle par code / canal / langue. */
    detail: (code: string, channel: string, language: string) =>
      `/gw/notification/api/v1/NotificationTemplates/${code}/${channel}/${language}`,
    /**
     * POST — bascule active/inactive.
     * `isActive` est un booléen NON NULLABLE en query : l'omettre équivaut
     * à envoyer `false` (guide §6.7). Toujours joindre le paramètre.
     */
    active: (code: string, channel: string, language: string) =>
      `/gw/notification/api/v1/NotificationTemplates/${code}/${channel}/${language}/active`,
  },
  preferences: {
    /** GET — préférences des trois canaux d'une identité (défaut : activés). */
    byParty: (partyId: string) =>
      `/gw/notification/api/v1/NotificationPreferences/party/${partyId}`,
    /**
     * PUT — active/désactive un canal pour une identité.
     * `isEnabled` est un booléen NON NULLABLE en query : l'omettre équivaut
     * à désactiver (guide §6.7). Toujours joindre le paramètre.
     */
    updateChannel: (partyId: string, channel: string) =>
      `/gw/notification/api/v1/NotificationPreferences/party/${partyId}/${channel}`,
  },
  logs: {
    /** POST — historique d'envoi paginé, corps `ListRequest<TFilter>` (§4.2). */
    all: "/gw/notification/api/v1/NotificationLogs/all",
  },
  devices: {
    /**
     * POST — enregistre ou rafraîchit l'appareil PUSH de l'utilisateur JWT.
     * Appel technique (hors ShareablePreview). 201 premier enregistrement, 200 ensuite.
     */
    add: "/gw/notification/api/v1/NotificationDevices/add",
    /**
     * POST — variante body (deviceId et/ou fcmToken). 204 idempotent.
     * Déclaré pour parité Postman ; le back-office utilise `byId` (DELETE).
     */
    remove: "/gw/notification/api/v1/NotificationDevices/remove",
    /** DELETE — désenregistre l'appareil. 204 même si inconnu. */
    byId: (deviceId: string) =>
      `/gw/notification/api/v1/NotificationDevices/${deviceId}`,
  },
  events: {
    /** POST — flashpay-superadmin, flashpay-service. Déclenche notif KYC approuvé. */
    simulateKycCaseApproved:
      "/gw/notification/api/v1/NotificationEvents/simulate-kyc-case-approved",
    /** POST — flashpay-superadmin, flashpay-service. Déclenche notif KYC rejeté. */
    simulateKycCaseRejected:
      "/gw/notification/api/v1/NotificationEvents/simulate-kyc-case-rejected",
    /** POST — flashpay-superadmin, flashpay-service. Déclenche notif KYC expiré. */
    simulateKycCaseExpired:
      "/gw/notification/api/v1/NotificationEvents/simulate-kyc-case-expired",
    /** POST — flashpay-superadmin, flashpay-service. Déclenche notif transaction aboutie. */
    simulateWalletTransactionCompleted:
      "/gw/notification/api/v1/NotificationEvents/simulate-wallet-transaction-completed",
    /** POST — flashpay-superadmin, flashpay-service. Déclenche notif transaction en échec. */
    simulateWalletTransactionFailed:
      "/gw/notification/api/v1/NotificationEvents/simulate-wallet-transaction-failed",
    /**
     * POST — guide v2.5 §6.7. Point d'entrée générique des notifications
     * déclenchées par topic Kafka — remplace à terme les `simulate-*` typés.
     * Rôles : flashpay-service, flashpay-superadmin, flashpay-admin.
     */
    fire: "/gw/notification/api/v1/NotificationEvents/event",
  },
  /**
   * Registre de routage des fournisseurs de transport (SMSPRO / XTechnology / SMTP / FCM).
   * Guide v2.3 §6.7. Rôles : flashpay-superadmin, flashpay-admin.
   */
  providers: {
    /** POST — crée une ligne de routage (code MAJUSCULES, config sans secret). */
    add: "/gw/notification/api/v1/NotificationProviders/add",
    /** POST — liste paginée du registre, corps `ListRequest<NotificationProviderFilter>`. */
    all: "/gw/notification/api/v1/NotificationProviders/all",
    /** GET — détail d'une ligne. */
    detail: (notificationProviderId: string) =>
      `/gw/notification/api/v1/NotificationProviders/${notificationProviderId}`,
    /** PUT — met à jour une ligne (poids, priorité, activation, options). */
    update: (notificationProviderId: string) =>
      `/gw/notification/api/v1/NotificationProviders/${notificationProviderId}`,
    /** DELETE — supprime une ligne. */
    remove: (notificationProviderId: string) =>
      `/gw/notification/api/v1/NotificationProviders/${notificationProviderId}`,
  },
  /**
   * Envoi direct et synchrone d'une notification à partir d'un gabarit (guide
   * v2.3 §6.7). **Service-à-service** (émetteur principal = user-service pour
   * les OTP) — déclaré pour parité, pas d'écran back-office.
   */
  notifications: {
    /** POST — flashpay-service, flashpay-superadmin, flashpay-admin. */
    send: "/gw/notification/api/v1/Notifications/send",
  },
  /**
   * Expédition SMTP d'un relevé composé (guide v2.3 §6.7). **Service-à-service**
   * (émis par wallet-service depuis `Statements/request`) — déclaré pour parité.
   */
  statementEmails: {
    /** POST — flashpay-service, flashpay-superadmin, flashpay-admin. */
    send: "/gw/notification/api/v1/StatementEmails/send",
  },
} as const;
